// src/components/inventory/InventoryQuantityDialog.tsx
// Il modalino che chiede "quanti pezzi?" prima di segnare un uso o una vendita.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, LucideIcon } from 'lucide-react';

import Modal from '@/components/ui/Modal';
import QuantityInput from '@/components/ui/QuantityInput';

const labelCls = 'block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2';

interface InventoryQuantityDialogProps {
    isOpen: boolean;
    title: string;
    subtitle?: string;
    icon: LucideIcon;
    /** Giacenza disponibile: oltre non si può scendere. */
    max: number;
    confirmLabel: string;
    loading?: boolean;
    onConfirm: (quantity: number) => void;
    onCancel: () => void;
}

const InventoryQuantityDialog: React.FC<InventoryQuantityDialogProps> = ({
    isOpen,
    title,
    subtitle,
    icon: Icon,
    max,
    confirmLabel,
    loading = false,
    onConfirm,
    onCancel,
}) => {
    const { t } = useTranslation();
    const [quantity, setQuantity] = useState(1);

    useEffect(() => {
        if (isOpen) setQuantity(1);
    }, [isOpen]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={loading ? () => undefined : onCancel}
            compact
            className="max-w-sm"
            headerClassName="bg-white"
            closeButtonClassName="bg-gray-100 hover:bg-gray-200 text-gray-500"
            headerContent={
                <div className="flex items-center gap-3 pr-8">
                    <div className="w-11 h-11 rounded-2xl bg-clip-50 text-clip-600 flex items-center justify-center flex-shrink-0">
                        <Icon size={20} />
                    </div>
                    <div className="min-w-0">
                        <h2 className="text-lg font-bold text-gray-900 truncate">{title}</h2>
                        {subtitle && <p className="text-sm text-gray-400 truncate">{subtitle}</p>}
                    </div>
                </div>
            }
        >
            <div className="space-y-4">
                <div>
                    <label className={labelCls}>{t('inventory.quantity', 'Quantità')}</label>
                    <QuantityInput value={quantity} onChange={setQuantity} max={max} disabled={loading} />
                    <p className="mt-2 text-xs text-gray-400">
                        {t('inventory.available', 'Disponibili: {{n}} pz', { n: max })}
                    </p>
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={loading}
                        className="px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 font-bold text-sm transition-colors disabled:opacity-50"
                    >
                        {t('common.cancel', 'Annulla')}
                    </button>
                    <button
                        type="button"
                        onClick={() => onConfirm(quantity)}
                        disabled={loading || max <= 0}
                        className="flex items-center gap-2 px-5 py-2.5 bg-clip-500 hover:bg-clip-600 text-white font-bold rounded-xl text-sm transition-all active:scale-95 disabled:opacity-50 disabled:active:scale-100"
                    >
                        {loading && <Loader2 size={16} className="animate-spin" />}
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default InventoryQuantityDialog;
